/**
 *
 * ResourceRegister
 *
 * IMPORTANT NOTE!!  This component is also used by the QResourcePicker, as a generic tree list display with
 * The ability to select items.  It is used for accounts, tags, and payees as well as categories
 *
 */

import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';
import classNames from 'classnames';
import QCheckbox from 'components/QCheckbox';
import { CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE } from 'components/QFilterSelect/constants';
import TreeNodeRow from 'components/ResourceRegister/TreeNodeRow';
import { resourceRegisterStyles as styles } from 'components/ResourceRegister/styles';
import SearchBox from 'components/SearchBox';
import PropTypes from 'prop-types';
import React from 'react';
import { withStyles } from 'tss-react/mui';
import { handleKeyDownOnSelector } from './utils';

const handleKeyDown = handleKeyDownOnSelector(
  'li[tabindex="0"][id*="row-item"], img[tabindex="0"], button[id="show-more-button"], input[id="search"]',
);

const INITIAL_STATE = {
  editingId: null,
  cancelEditingId: null,
};

class ResourceRegister extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = { ...INITIAL_STATE };
  }

  componentDidMount() {
    document.addEventListener('keydown', handleKeyDown);
  }

  componentWillUnmount = () => {
    document.removeEventListener('keydown', handleKeyDown);
  };

  requestEditing = (editingId) => {
    const { editingId: sEditingId, cancelEditingId: sCancelEditingId } = this.state;

    this.setState({
      editingId,
      cancelEditingId: sEditingId !== editingId ? sEditingId : sCancelEditingId,
    });
  };

  cleanEditingData = (editingId) => {
    this.setState({
      ...INITIAL_STATE,
      editingId: editingId || INITIAL_STATE.editingId,
    });
  };

  renderHeader() {
    const { classes, dataDictionary } = this.props;
    return (
      <div className={classes.headerContainer}>
        {dataDictionary.map((row, index) => {
          const labelStyles = row.type === 'actions' ? { minWidth: 140 } : {};
          return (
            <Typography
              key={row.id}
              style={{ ...labelStyles, flex: row.flex }}
              variant='bodyMedium'
              className={classNames(classes.baseRowHead, index === 0 ? classes.firstRowHead : classes.alignmentRowHead)}
            >
              {row.label}
            </Typography>
          );
        })}
      </div>
    );
  }

  calculateDataSize(data, resourceType) {
    if (resourceType !== 'categories' && resourceType !== CLIENTS_AND_PROJECTS_FILTER_RESOURCE_TYPE) {
      return data?.size;
    }
    return (
      data?.reduce((acc, item) => {
        let size = 1;
        if (item.children) {
          size += this.calculateDataSize(item.children, resourceType);
        }
        return acc + size;
      }, 0) || 0
    );
  }

  renderRows() {
    const {
      classes,
      dataDictionary,
      data,
      onSave,
      onDelete,
      onRemoveNewItem,
      getCategoryByID,
      narrowMode,
      setDialog,
      onSelect,
      selectedItems,
      onSearchChange,
      resourceType,
      autoFocus,
      children,
      placeholder,
      noBorder,
      searchChildren,
      selectAll = false,
    } = this.props;

    const dataSize = this.calculateDataSize(data, resourceType);
    const placeholderOverrides = {
      payeeNames: 'payees',
    };
    const placeholderDefault = resourceType ? placeholderOverrides[resourceType] || resourceType : 'items';

    return (
      <div className={classes.rowsContainer}>
        {children}
        {onSearchChange && (
          <SearchBox
            classes={{ smartSearch: noBorder ? classes.smartSearchNoBorder : classes.smartSearch }}
            onSearch={onSearchChange}
            autoFocus={autoFocus}
            placeholder={placeholder || `Select ${placeholderDefault}`}
            autoSearch
          />
        )}
        {noBorder && <Divider />}
        {selectAll && (
          <ListItem
            tabIndex={0}
            id={'select-all-row-item'}
            // className={itemClasses}
            onClick={null}
            onKeyDown={null}
          >
            <QCheckbox
              tabIndex={-1}
              disabled={false}
              onChange={() => onSelect(data)}
              checked={dataSize === selectedItems.size}
              indeterminate={dataSize !== selectedItems.size && selectedItems.size !== 0}
              id={'select-all-select-box'}
              className={classes.actionIcon}
              aria-label='Select'
              classes={{ root: classes.checkboxScale }}
            />
            <Typography id={'select-all-text'} variant='bodyRegular'>
              Select all
            </Typography>
          </ListItem>
        )}
        <Divider />
        {data.map((item, index) => (
          <TreeNodeRow
            key={item.id}
            narrowMode={narrowMode}
            item={item}
            rowIndex={[index]}
            dataDictionary={dataDictionary}
            onSave={onSave}
            onDelete={onDelete}
            onRemoveNewItem={onRemoveNewItem}
            getCategoryByID={getCategoryByID}
            setDialog={setDialog}
            onSelect={onSelect}
            selectedItems={selectedItems}
            expandForSearch={searchChildren}
            editingProps={{
              editingId: this.state.editingId,
              cancelEditingId: this.state.cancelEditingId,
              requestEditing: this.requestEditing,
              cleanEditingData: this.cleanEditingData,
            }}
            resourceType={resourceType}
          />
        ))}
      </div>
    );
  }

  render() {
    const { classes, noHeader } = this.props;

    return (
      <div className={classes.rootContainer}>
        {!noHeader ? this.renderHeader() : ''}
        {this.renderRows()}
      </div>
    );
  }
}

ResourceRegister.propTypes = {
  classes: PropTypes.object.isRequired,
  data: PropTypes.object,
  narrowMode: PropTypes.bool,
  dataDictionary: PropTypes.object,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  onSelect: PropTypes.func,
  selectedItems: PropTypes.object,
  onRemoveNewItem: PropTypes.func,
  getCategoryByID: PropTypes.func,
  setDialog: PropTypes.func,
  noHeader: PropTypes.bool,
  autoFocus: PropTypes.bool,
  onSearchChange: PropTypes.func,
  resourceType: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  placeholder: PropTypes.string,
  noBorder: PropTypes.bool,
  searchChildren: PropTypes.bool,
  selectAll: PropTypes.bool,
};

export default withStyles(ResourceRegister, styles);
